import { RECIPE_COURSES, formatCourse, formatCuisine, type RecipeCourse } from '@mlc/shared';
import { useEffect, useState } from 'react';
import type { CollectionQuery, CollectionWithQuery, ListTagsResponse, TagWithCount } from '@mlc/shared';
import { createCollection, deleteCollection, listCollections, listCuisines, listTags } from './api';
import { navigate, withSearchParams } from './router';
import { Badge, Button, INTERACTIVE, Icon, Select, TAG_TONE_CLASSES, TextInput } from './ui';

/**
 * Barre de filtres de la bibliothèque (US-21).
 *
 * Le défaut corrigé : les quatre seules étiquettes existantes étaient
 * calculées, présentées comme quatre puces visuellement identiques, et
 * mélangeaient quatre natures — « à refaire » est un jugement, « sans four »
 * un équipement, « jamais cuisinée » un historique, « moins de 30 min » une
 * durée. Rien ne permettait à l'utilisateur de ranger quoi que ce soit
 * lui-même.
 *
 * Les filtres sont désormais **groupés et libellés** par nature. Quatre
 * groupes plutôt que les trois du brief : les étiquettes manuelles ont leur
 * propre groupe (« Mes étiquettes ») au lieu d'être fondues dans les
 * collections. Ce sont deux choses différentes — une étiquette est posée
 * recette par recette, une collection est un critère mémorisé — et les
 * confondre aurait reproduit à l'identique le défaut que la story corrige.
 */

/**
 * La répartition des étiquettes en groupes **vient du serveur** depuis US-61
 * (`TagWithCount.portee`).
 *
 * Elle était écrite ici, en dur : `const HISTORY_TAGS = ['jamais cuisinée',
 * 'à refaire']`, face à la même liste côté serveur — deux tableaux de libellés
 * littéraux à tenir synchrones. Les libellés étant devenus renommables, la
 * liste locale devenait fausse au premier renommage : « déjà faite ? » serait
 * tombée dans « Contraintes », à côté de « sans four ».
 */

export interface LibraryFiltersProps {
  /** Terme de recherche courant, pour l'enregistrement d'une collection. */
  query: string;
  status: string;
  /** Noms d'étiquettes actives (US-21), cumulées par ET. */
  tags: string[];
  url: string;
  /** Incrémenté quand la bibliothèque change : les comptes d'étiquettes doivent suivre. */
  refreshKey: number;  /** Nature du plat sélectionnée (US-55), `''` si aucune. */
  course: string;
  /** Cuisine sélectionnée (US-55), `''` si aucune. */
  cuisine: string;
  /**
   * Le tri courant (US-22), `''` pour « ajout récent » (US-105).
   *
   * **Il a rejoint ce panneau.** Il vivait dans la barre d'outils, à côté de
   * la recherche, sur sa propre rangée dès que l'écran devenait étroit : trois
   * contrôles empilés avant la première recette, sur un téléphone où la
   * première carte commençait au deuxième écran. Trier et filtrer répondent à
   * la même question — « montre-moi cette liste autrement » — et les séparer
   * était un accident d'historique, pas une décision.
   */
  sort: string;
}

/** Une puce de filtre. Bouton et non lien : elle bascule un critère, elle ne mène pas à une adresse différente de la bibliothèque. */
function FilterChip({
  label,
  count,
  active,
  tone,
  onToggle,
}: {
  label: string;
  count?: number;
  active: boolean;
  tone: 'auto' | 'manual' | 'status';
  onToggle: () => void;
}): React.JSX.Element {
  const toneClasses = tone === 'status' ? 'bg-surface-raised text-foreground-secondary' : TAG_TONE_CLASSES[tone];
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onToggle}
      // `INTERACTIVE` (US-54) : ces puces sont neuf des vingt contrôles de
      // l'accueil, et n'avaient ni anneau de focus ni retour au survol — elles
      // étaient donc inatteignables au clavier autrement qu'à l'aveugle.
      // L'état actif ne se signale plus trois fois : la bordure suffit sur un
      // fond déjà coloré, l'anneau de 2 px par-dessus faisait du bruit.
      className={`min-h-touch rounded-full border px-3 text-sm font-medium ${INTERACTIVE} ${toneClasses} ${
        active ? 'border-primary' : 'border-border-interactive'
      }`}
    >
      {label}
      {count !== undefined && <span className="text-data ml-1 opacity-70">{count}</span>}
    </button>
  );
}

/** Un groupe de filtres, avec son intitulé. C'est l'intitulé qui fait la moitié du travail : il dit de quelle nature est le critère. */
function FilterGroup({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-semibold tracking-wide text-foreground-muted uppercase">{label}</span>
      {children}
    </div>
  );
}

/** Deux critères identiques ? Comparaison par contenu, l'ordre des étiquettes n'ayant pas de sens. */
function sameQuery(a: CollectionQuery, b: CollectionQuery): boolean {
  const normalize = (q: CollectionQuery): string =>
    JSON.stringify({ q: q.q ?? '', status: q.status ?? '', tags: [...(q.tags ?? [])].sort() });
  return normalize(a) === normalize(b);
}

export function LibraryFilters({ query, status, tags, course, cuisine, sort, url, refreshKey }: LibraryFiltersProps): React.JSX.Element {
  const [allTags, setAllTags] = useState<ListTagsResponse>([]);
  const [cuisines, setCuisines] = useState<string[]>([]);
  // Ouvert si un filtre est déjà posé : arriver sur une liste courte sans
  // voir pourquoi serait pire que la densité qu'on cherche à réduire.
  const [ouvert, setOuvert] = useState(
    status !== '' || tags.length > 0 || course !== '' || cuisine !== '',
  );
  const [collections, setCollections] = useState<CollectionWithQuery[]>([]);
  const [savingName, setSavingName] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  /**
   * Échap referme le panneau (US-105).
   *
   * Il ne le fallait pas tant qu'il vivait dans le flux : un bloc qui pousse
   * la page se referme en refaisant le geste qui l'a ouvert. Superposé, il
   * recouvre la liste, et tout ce qui recouvre se ferme par Échap — sinon la
   * seule sortie est un bouton qu'on ne voit plus si l'on a fait défiler.
   */
  useEffect(() => {
    if (!ouvert) return undefined;
    function surTouche(evenement: KeyboardEvent): void {
      if (evenement.key === 'Escape') setOuvert(false);
    }
    window.addEventListener('keydown', surTouche);
    return () => { window.removeEventListener('keydown', surTouche); };
  }, [ouvert]);

  useEffect(() => {
    const controller = new AbortController();
    listTags(controller.signal).then(setAllTags).catch(() => { /* la barre de filtres n'est pas l'écran : son échec ne doit rien bloquer */ });
    listCollections(controller.signal).then(setCollections).catch(() => { /* idem */ });
    // Cuisines réellement présentes dans le périmètre visible (US-55) : la
    // liste vient du serveur et n'est jamais devinée côté client.
    Promise.resolve().then(() => listCuisines()).then(setCuisines).catch(() => { /* idem */ });
    return () => { controller.abort(); };
  }, [refreshKey]);

  const currentQuery: CollectionQuery = {
    ...(query === '' ? {} : { q: query }),
    ...(status === '' ? {} : { status: status as CollectionQuery['status'] }),
    ...(tags.length === 0 ? {} : { tags }),
  };
  const hasFilters = query !== '' || status !== '' || tags.length > 0;

  function toggleTag(name: string): void {
    const next = tags.includes(name) ? tags.filter((t) => t !== name) : [...tags, name];
    navigate(withSearchParams(url, { etiquettes: next.join(',') }), { replace: true });
  }

  function toggleStatus(value: string): void {
    navigate(withSearchParams(url, { statut: status === value ? '' : value }), { replace: true });
  }

  // Nature du plat et cuisine (US-55). **Une seule valeur à la fois** : une
  // recette est une entrée ou un dessert, cocher les deux ne veut rien dire —
  // contrairement aux étiquettes, qui se cumulent par ET.
  function effacerTout(): void {
    navigate(
      withSearchParams(url, { statut: '', etiquettes: '', type: '', cuisine: '' }),
      { replace: true },
    );
  }

  function toggleCourse(value: string): void {
    navigate(withSearchParams(url, { type: course === value ? '' : value }), { replace: true });
  }

  function toggleCuisine(value: string): void {
    navigate(withSearchParams(url, { cuisine: cuisine === value ? '' : value }), { replace: true });
  }

  function applyCollection(collection: CollectionWithQuery): void {
    const active = sameQuery(currentQuery, collection.query);
    // Rejouer une collection **remplace** les filtres courants au lieu de s'y
    // ajouter : « repas de semaine » est un point de départ complet, pas un
    // critère de plus. Un second clic revient à la bibliothèque entière.
    navigate(
      withSearchParams(url, {
        q: active ? '' : collection.query.q ?? '',
        statut: active ? '' : collection.query.status ?? '',
        etiquettes: active ? '' : (collection.query.tags ?? []).join(','),
      }),
      { replace: true },
    );
  }

  function saveCurrentAsCollection(name: string): void {
    setSaveError(null);
    createCollection({ name, query: currentQuery })
      .then((created) => {
        setCollections((previous) => [...previous, created]);
        setSavingName(null);
      })
      .catch((error: unknown) => { setSaveError(error instanceof Error ? error.message : 'Enregistrement impossible.'); });
  }

  function removeCollection(collection: CollectionWithQuery): void {
    deleteCollection(collection.id)
      .then(() => { setCollections((previous) => previous.filter((c) => c.id !== collection.id)); })
      .catch(() => { /* la collection reste affichée : mieux vaut un rangement en trop qu'un écran cassé */ });
  }

  const statusTags = allTags.filter((tag) => tag.portee === 'journal');
  const constraintTags = allTags.filter((tag) => tag.portee === 'recette');
  const manualTags = allTags.filter((tag) => tag.portee === 'manuelle');

  const actifs = [
    ...(status === '' ? [] : [status === 'a_cuisiner' ? 'À cuisiner' : 'Favoris']),
    ...tags,
    ...(course === '' ? [] : [formatCourse(course as RecipeCourse) ?? course]),
    ...(cuisine === '' ? [] : [formatCuisine(cuisine) ?? cuisine]),
  ];

  return (
    <section aria-labelledby="filtres-titre" className="flex flex-col gap-3">
      <h2 id="filtres-titre" className="sr-only">Filtrer les recettes</h2>

      {/* **Repliés par défaut** (US-56). Dépliés, les quatre groupes portaient
          entre quinze et vingt puces sur un écran qui en comptait déjà vingt
          avant la première recette — et US-55 en a ajouté six de plus avec la
          nature du plat. Ce qu'on veut voir en ouvrant l'application, ce sont
          des recettes ; les filtres sont un geste, pas un décor. Les filtres
          actifs restent visibles replié, sinon on ne comprendrait pas pourquoi
          la liste est courte. */}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant={ouvert ? 'primary' : 'secondary'}
          size="compact"
          aria-expanded={ouvert}
          aria-controls="filtres-groupes"
          onClick={() => { setOuvert((precedent) => !precedent); }}
        >
          <Icon name="filtre" size={0.9} />
          Filtrer
        </Button>
        {!ouvert && actifs.map((libelle) => (
          <Badge key={libelle} tone="auto">{libelle}</Badge>
        ))}
        {!ouvert && actifs.length > 0 && (
          <Button type="button" variant="ghost" size="compact" onClick={effacerTout}>
            Tout effacer
          </Button>
        )}
      </div>

      {/* `hidden` et non le dépliage animé de `.disclosure` (index.css) :
          l'animation demande que le contenu reste monté, or `navigation.test.tsx`
          mesure le nombre de contrôles de l'accueil **avant la première
          recette** — une règle d'US-56 — et des puces de filtre repliées mais
          présentes le feraient échouer à juste titre. Un panneau qui compte
          dans la densité de l'écran alors qu'on ne le voit pas est un défaut,
          pas une animation.

          **Superposé depuis US-105**, et non plus dans le flux. Deux raisons,
          et la seconde compte davantage que la première : déplié, il poussait
          la liste de deux cents pixels vers le bas, si bien que le geste
          « je filtre » commençait par faire disparaître ce qu'on filtrait ;
          et son déclencheur peut désormais tenir sur la même ligne que la
          recherche, ce qui rend à l'accueil la hauteur d'une carte entière.

          Il se cale sur la **barre d'outils** (`relative` dans
          `RecipeList.tsx`), pas sur son bouton : ancré au bouton, il
          s'ouvrirait large de six centimètres, aligné à droite. */}
      <div
        id="filtres-groupes"
        hidden={!ouvert}
        className="absolute inset-x-0 top-full z-40 mt-2 flex flex-col gap-3 rounded-card border border-border bg-surface p-3 shadow-overlay">
      {/* **Le tri, en tête du panneau** (US-105). En tête parce qu'il porte
          sur la liste entière là où les puces ci-dessous la réduisent : on
          choisit d'abord dans quel ordre on regarde, ensuite ce qu'on
          regarde. Une liste déroulante et non des puces — cinq critères
          exclusifs, dont un est toujours actif, ne sont pas des filtres qu'on
          cumule. */}
      <FilterGroup label="Trier par">
        <Select
          aria-label="Trier les recettes"
          value={sort === '' ? 'recent' : sort}
          onChange={(e) => {
            navigate(withSearchParams(url, { tri: e.target.value === 'recent' ? '' : e.target.value }), { replace: true });
          }}
          className="w-auto bg-surface"
        >
          <option value="recent">Ajout récent</option>
          <option value="alpha">Ordre alphabétique</option>
          <option value="duree">Temps total</option>
          <option value="note">Mieux notées</option>
          <option value="oublie">Pas cuisinées depuis longtemps</option>
        </Select>
      </FilterGroup>

      <FilterGroup label="Statut">
        <FilterChip
          label="À cuisiner"
          active={status === 'a_cuisiner'}
          tone="status"
          onToggle={() => { toggleStatus('a_cuisiner'); }}
        />
        <FilterChip
          label="Favoris"
          active={status === 'favorite'}
          tone="status"
          onToggle={() => { toggleStatus('favorite'); }}
        />
        {/* « Jamais cuisinée » et « à refaire » restent des étiquettes
            calculées : ce sont des faits déduits du journal, pas des
            intentions. Elles se manipulent comme les autres filtres, mais
            elles ne sont pas stockées sur la recette. */}
        {statusTags.map((tag) => (
          <FilterChip
            key={tag.id}
            label={tag.name}
            count={tag.recipe_count}
            active={tags.includes(tag.name)}
            tone="auto"
            onToggle={() => { toggleTag(tag.name); }}
          />
        ))}
      </FilterGroup>

      {/* Ce que la recette **est** (US-55). Distinct des étiquettes juste
          en dessous, et pour la même raison qui a fait séparer étiquettes et
          collections en US-21 : les confondre reproduirait le défaut. */}
      <FilterGroup label="Nature">
        {RECIPE_COURSES.map((valeur) => (
          <FilterChip
            key={valeur}
            label={formatCourse(valeur) ?? valeur}
            active={course === valeur}
            tone="status"
            onToggle={() => { toggleCourse(valeur); }}
          />
        ))}
      </FilterGroup>

      {cuisines.length > 0 && (
        <FilterGroup label="Cuisines">
          {cuisines.map((nom) => (
            <FilterChip
              key={nom}
              label={formatCuisine(nom) ?? nom}
              active={cuisine === nom}
              tone="status"
              onToggle={() => { toggleCuisine(nom); }}
            />
          ))}
        </FilterGroup>
      )}

      {constraintTags.length > 0 && (
        <FilterGroup label="Contraintes">
          {constraintTags.map((tag: TagWithCount) => (
            <FilterChip
              key={tag.id}
              label={tag.name}
              count={tag.recipe_count}
              active={tags.includes(tag.name)}
              tone="auto"
              onToggle={() => { toggleTag(tag.name); }}
            />
          ))}
        </FilterGroup>
      )}

      {manualTags.length > 0 && (
        <FilterGroup label="Mes étiquettes">
          {manualTags.map((tag) => (
            <FilterChip
              key={tag.id}
              label={tag.name}
              count={tag.recipe_count}
              active={tags.includes(tag.name)}
              tone="manual"
              onToggle={() => { toggleTag(tag.name); }}
            />
          ))}
        </FilterGroup>
      )}

      <FilterGroup label="Collections">
        {collections.map((collection) => (
          <span key={collection.id} className="flex items-center gap-2">
            <FilterChip
              label={collection.name}
              active={sameQuery(currentQuery, collection.query)}
              tone="status"
              onToggle={() => { applyCollection(collection); }}
            />
            <button
              type="button"
              onClick={() => { removeCollection(collection); }}
              aria-label={`Supprimer la collection ${collection.name}`}
              className="size-touch rounded-full text-foreground-muted hover:text-danger"
            >
              <Icon name="croix" />
            </button>
          </span>
        ))}

        {/* Enregistrer les filtres courants : c'est le seul moyen de créer une
            collection, et c'est le bon moment — on vient de composer le
            filtrage qu'on veut retrouver. */}
        {savingName === null ? (
          hasFilters && (
            <Button type="button" variant="ghost" size="compact" onClick={() => { setSavingName(''); }}>
              Enregistrer ces filtres
            </Button>
          )
        ) : (
          <span className="flex items-center gap-2">
            <label htmlFor="collection-name" className="sr-only">
              Nom de la collection
            </label>
            <TextInput
              id="collection-name"
              value={savingName}
              autoFocus
              placeholder="Repas de semaine"
              onChange={(e) => { setSavingName(e.target.value); }}
              className="w-48"
            />
            <Button
              type="button"
              variant="secondary"
              size="compact"
              disabled={savingName.trim() === ''}
              onClick={() => { saveCurrentAsCollection(savingName.trim()); }}
            >
              Enregistrer
            </Button>
            <Button type="button" variant="ghost" size="compact" onClick={() => { setSavingName(null); setSaveError(null); }}>
              Annuler
            </Button>
          </span>
        )}
        {saveError !== null && <span className="text-sm text-danger">{saveError}</span>}
      </FilterGroup>
      </div>
    </section>
  );
}
